import { Avatar } from '../../ui/Avatar.js';
import { cx } from '../../ui/cx.js';
import { ChevronDownIcon } from '../../ui/icons.js';
import { MenuButton, type MenuItem } from '../../ui/Menu.js';

/**
 * Menu de la personne connectée : « Mon profil », « Changer de garage », « Se déconnecter »
 * (ou « Quitter le mode support »). Trois présentations :
 * - `sidebar` : bloc du bas de la barre latérale (ordinateur), aux couleurs de la barre ;
 * - `masthead` : avatar de l'en-tête (téléphone), aux couleurs de l'en-tête ;
 * - `plain` : en-tête neutre (console plateforme).
 */
export function AccountMenu({
  name,
  email,
  detail,
  tone,
  items,
  variant,
}: {
  name: string;
  email: string;
  /** Rôle ou garage, sous le nom. */
  detail: string;
  tone?: 'author-support';
  items: MenuItem[];
  variant: 'sidebar' | 'masthead' | 'plain';
}) {
  const header = (
    <div className="min-w-0 leading-tight">
      <p className="truncate text-sm font-medium text-ink">{name}</p>
      <p className="mt-0.5 truncate text-xs text-ink-3">{email}</p>
    </div>
  );
  const label = `Menu du compte, ${name}`;

  if (variant === 'sidebar') {
    return (
      <MenuButton
        label={label}
        items={items}
        header={header}
        placement="above-start"
        menuClassName="w-[calc(var(--sidebar-w)-1.5rem)]"
        className="group flex min-h-(--ctl-sm) w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-chrome-hover focus-visible:outline-chrome-focus aria-expanded:bg-chrome-hover"
      >
        <Avatar name={name} size="sm" tone={tone} />
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block truncate text-sm font-medium text-on-chrome">{name}</span>
          <span className="block truncate text-xs text-on-chrome-3">{detail}</span>
        </span>
        <ChevronDownIcon size={16} className="shrink-0 rotate-180 text-on-chrome-3 transition-transform group-aria-expanded:rotate-0" />
      </MenuButton>
    );
  }

  return (
    <MenuButton
      label={label}
      items={items}
      header={header}
      placement="below-end"
      className={cx(
        'grid size-11 place-items-center rounded-full transition-colors duration-150',
        variant === 'masthead'
          ? 'hover:bg-on-masthead/12 focus-visible:outline-on-masthead aria-expanded:bg-on-masthead/12'
          : 'hover:bg-sunken aria-expanded:bg-sunken',
      )}
    >
      <Avatar name={name} size="sm" tone={tone} />
    </MenuButton>
  );
}
